import React, { useState, useEffect, useCallback, useRef } from 'react';
import classnames from 'classnames';
import Autosuggest from 'react-autosuggest';
import InfiniteScroll from 'react-infinite-scroll-component';
import { debounce } from 'throttle-debounce';
import { SearchIcon, Loader, Scrollbar, PlusInCircleRedIcon } from 'components';
import { highlightText } from 'utils/format/highlightText';
import ConditionalWrapper from 'components/ConditionalWrapper';

import './search.scss';

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function getSuggestionLabel(suggestion) {
  if (typeof suggestion === 'string') {
    return suggestion;
  }
  if (suggestion && typeof suggestion === 'object') {
    return suggestion.label || '';
  }
  return '';
}

function getSuggestionValue(suggestion) {
  return getSuggestionLabel(suggestion);
}

function renderSuggestion(suggestion, { query }) {
  if (suggestion && typeof suggestion === 'object' && suggestion.rendered) {
    return suggestion.rendered;
  }

  const suggestionLabel = getSuggestionLabel(suggestion);
  const parts = highlightText(suggestionLabel, query);
  return (
    <span className="react-autosuggest__suggestion-text">
      {parts.map((part, index) => (
        <span
          className={classnames({
            'react-autosuggest__suggestion-match': part.highlight,
          })}
          key={index}
        >
          {part.text}
        </span>
      ))}
    </span>
  );
}

function renderSuggestionStudies(suggestion, { query }) {
  const suggestionLabel = getSuggestionLabel(suggestion);
  const parts = highlightText(suggestionLabel, query);
  return (
    <div className="react-autosuggest__suggestion-text_studies">
      <div>
        {parts.map((part, index) => (
          <span
            className={classnames({
              'react-autosuggest__suggestion-match': part.highlight,
            })}
            key={index}
          >
            {part.text}
          </span>
        ))}
      </div>
      <PlusInCircleRedIcon />
    </div>
  );
}

export default function SearchSimpleField(props = {}) {
  const {
    onInputChange,
    onInputDataChange,
    onChange: onSearchChange,
    value: initialValue = '',
    onSuggestionStudies = false,
    onSuggestionSelectedCallback = Function.prototype,
    searchInstrument = Function.prototype,
    showIcon = true,
    noResetValue,
    withSpaces,
    fieldClassName,
    withBlankRender,
    withInfinityScroll,
    dataTestId,
    getSuggestionLabel: getSuggestionLabelProp,
    getInputPostfix,
    mapExternalValueToInput,
    ...propsToPass
  } = props;

  const resolveSuggestionLabel = useCallback(
    (suggestion) =>
      typeof getSuggestionLabelProp === 'function'
        ? getSuggestionLabelProp(suggestion)
        : getSuggestionLabel(suggestion),
    [getSuggestionLabelProp],
  );
  const toInputValue = useCallback(
    (fieldValue) =>
      typeof mapExternalValueToInput === 'function'
        ? mapExternalValueToInput(fieldValue)
        : fieldValue || '',
    [mapExternalValueToInput],
  );

  const [value, setValue] = useState(toInputValue(initialValue));
  const [items, setItems] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [fetchedItems, setFetchedItems] = useState([]);
  const prevFetching = usePrevious(isFetching);
  const prevValue = usePrevious(value);

  useEffect(() => {
    if (value === '') {
      setFetchedItems([]);
      setIsFetching(false);
    }
    setHasMore(true);
  }, [value, items]);

  useEffect(() => {
    if (noResetValue) {
      setValue(toInputValue(initialValue));
    }
  }, [initialValue, toInputValue]);

  useEffect(() => {
    prevFetching ? setStopLoading(true) : setStopLoading(false);
  }, [isFetching]);

  const onSuggestionsFetchRequested = async ({ value, next }) => {
    const normalizedValue = typeof value === 'string' ? value : '';
    const escapedValue = normalizedValue.trim().replace(/\W/g, '');
    const spacesValue = normalizedValue.replaceAll(' ', '%20');

    if (!withBlankRender && escapedValue === '') {
      return setItems([]);
    }

    setIsFetching(true);

    let fetchedItems = '';

    if (withSpaces) {
      fetchedItems = await searchInstrument(spacesValue, next);
    } else {
      fetchedItems = await searchInstrument(escapedValue, next);
    }

    setFetchedItems(fetchedItems);
    setIsFetching(false);
    setItems(fetchedItems || []);
  };

  const debounceOnChange = useCallback(
    debounce(500, onSuggestionsFetchRequested),
    [],
  );

  // Reset the instruments
  const onSuggestionsClearRequested = () => {
    setStopLoading(false);
    setItems([]);
  };

  const inputPostfix =
    typeof getInputPostfix === 'function' ? getInputPostfix(value) : '';
  const valueForWidth = inputPostfix
    ? (value || '').trim().split(/\s+/)[0] || ''
    : value || '';
  const keyInputStyle = inputPostfix
    ? {
        width: `${Math.max(valueForWidth.length + 2, 2)}ch`,
      }
    : undefined;

  const renderInputComponent = (inputProps) => (
    <div
      className={classnames('input-component', {
        'input-component_with-postfix': Boolean(inputPostfix),
      })}
    >
      <div className="input-component__text-group">
        <input
          {...inputProps}
          data-testid={
            dataTestId
              ? `${dataTestId}-search-input`
              : 'search-simple-field-input'
          }
          style={keyInputStyle}
        />
        {inputPostfix ? (
          <span className="input-component__postfix">{inputPostfix}</span>
        ) : null}
      </div>
      {!isFetching && showIcon && (
        <SearchIcon className="search-component__search-icon" />
      )}
      {isFetching && (
        <Loader
          transparent={true}
          isShow={true}
          className="search-component__loader"
          centeredLoader
          spinnerStyles={{
            width: '15px',
            height: '15px',
            left: 'auto',
            right: '0',
          }}
        >
          <div
            style={{ width: '15px', height: '15px', margin: '4px 0 4px 0' }}
          />
        </Loader>
      )}
    </div>
  );

  const fetchData = async () => {
    await onSuggestionsFetchRequested({ value, next: true });
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    const height =
      children && items.length
        ? items.length * 64
        : value !== '' &&
          stopLoading &&
          prevValue !== '' &&
          fetchedItems.length === 0
        ? 20
        : 0;
    const forInfinityScrollProps = withInfinityScroll && {
      scrollerProps: {
        renderer: (props) => {
          const { elementRef, ...restProps } = props;
          return (
            <div
              {...restProps}
              ref={elementRef}
              id="MyAwesomeScrollbarsScroller"
              className="MyAwesomeScrollbarsScroller"
            />
          );
        },
      },
    };

    return (
      <div
        {...containerProps}
        style={{ height: `${height}px`, paddingBottom: '10x' }}
      >
        {items.length > 0 ? (
          <Scrollbar {...forInfinityScrollProps}>
            <ConditionalWrapper
              condition={withInfinityScroll}
              children={children}
              wrapper={(children) => (
                <InfiniteScroll
                  dataLength={items.length}
                  next={fetchData}
                  hasMore={hasMore}
                  scrollableTarget={'MyAwesomeScrollbarsScroller'}
                >
                  {children}
                </InfiniteScroll>
              )}
            />
          </Scrollbar>
        ) : (
          'No results found for your request'
        )}
      </div>
    );
  };

  // Select and throw instrument for Chart component
  const onSuggestionSelected = (event, { suggestion }) => {
    const suggestionLabel = resolveSuggestionLabel(suggestion);
    onSearchChange(suggestionLabel);
    onInputDataChange(suggestionLabel, suggestion);
    onSuggestionSelectedCallback(suggestionLabel);

    if (!noResetValue) {
      setValue('');
    } else {
      setValue(suggestionLabel);
    }
  };

  const onChange = (event, { newValue }) => {
    if (newValue === '') {
      setStopLoading(false);
    }
    setValue(newValue);
    onSearchChange(newValue);
    if (typeof onInputDataChange === 'function') {
      onInputDataChange(newValue);
    }
    if (typeof onInputChange === 'function') {
      onInputChange(newValue);
    }
  };

  const shouldRenderSuggestions = () => {
    return true;
  };

  const renderSuggestionWithTestId = (suggestion, props) => {
    const rendered = renderSuggestionStudies(suggestion, props);
    if (dataTestId && rendered) {
      return React.cloneElement(rendered, {
        'data-testid': `${dataTestId}-suggestion`,
      });
    }
    return rendered;
  };

  return (
    <div
      className="search-component"
      data-testid={dataTestId ? `${dataTestId}-autocomplete` : undefined}
    >
      <Autosuggest
        suggestions={items}
        onSuggestionsFetchRequested={(suggestions) =>
          debounceOnChange(suggestions)
        }
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderSuggestions}
        getSuggestionValue={resolveSuggestionLabel}
        renderSuggestion={
          onSuggestionStudies ? renderSuggestionWithTestId : renderSuggestion
        }
        renderInputComponent={renderInputComponent}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          value,
          className: 'search-component__search-field',
          ...propsToPass,
          onChange,
        }}
      />
    </div>
  );
}
